import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  chatMatchesSearchQuery,
  getChatDocumentById,
  getChatsByIds,
  getChatsByOrderOrCaseNumber,
  getChatsForOrderDetails,
  getChatsForSearchWindow,
  getChatSearchTerms,
  getChatsQuery,
  MainFilters,
  matchesOfferDirection,
  messagesRef,
  normalizeChatSearchQuery,
  OFFER_DIRECTION_BY_FILTER,
} from '@util/firestore/messages';
import { getOffersByStatePaginated } from '@util/firestore/offers';
import { getOrderById } from '@util/firestore/orders';
import { getPublicUserDoc } from '@util/firestore/users';
import { useAuth } from 'context/AuthContext';
import { useConversations } from 'context/ConversationsContext';
import {
  getDocs,
  limit as limitFn,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { ChatDocument } from 'models/chat';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

const CHAT_SEARCH_STALE_TIME = 5 * 60 * 1000;

export interface UseTabChatsReturn {
  chats: ChatDocument[];
  loadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
}

export const useTabChats = (tab: MainFilters): UseTabChatsReturn => {
  const { userDoc } = useAuth();
  const pathname = usePathname();
  const { debouncedSearchQuery } = useConversations();
  const [chats, setChats] = useState<ChatDocument[]>([]);
  const [limit, setLimit] = useState(20);
  const isFilteredOffers = [
    'offers_pending',
    'offers_pending_received',
    'offers_pending_sent',
    'offers_accepted',
    'offers_declined',
    'offers_expired',
  ].includes(tab);
  const offerDirection = OFFER_DIRECTION_BY_FILTER[tab];
  const isOrderDetail =
    pathname?.includes('purchases') || pathname?.includes('orders');
  const orderId = pathname?.split('/').pop();
  const { data: order } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrderById(orderId as string),
    enabled: !!orderId && isOrderDetail,
  });
  const { data: chatsForOrderDetail = [] } = useQuery({
    queryKey: ['chatsForOrderDetail', orderId],
    queryFn: () => getChatsForOrderDetails(order!, userDoc!.uid),
    enabled: !!order && isOrderDetail && !!userDoc?.uid,
  });

  const isProfile = pathname?.includes('profile');
  const username_slug = pathname?.split('/').pop();
  const {
    data: chatsForProfile,
    fetchNextPage: fetchNextPageForProfile,
    hasNextPage: hasNextPageForProfile,
    isFetchingNextPage: isFetchingNextPageForProfile,
  } = useInfiniteQuery({
    queryKey: ['chatsForProfile', username_slug],
    queryFn: async ({ pageParam }) => {
      if (!username_slug) return [];
      const user = await getPublicUserDoc({ username_slug });
      if (!user) return [];
      const q = query(
        messagesRef,
        where('uids', 'array-contains', user.uid),
        orderBy('last_time', 'desc'),
        ...(pageParam ? [startAfter(pageParam)] : []),
        limitFn(20)
      );
      const snap = await getDocs(q);
      return snap.docs.map((doc) => doc.data() as ChatDocument);
    },
    enabled: !!username_slug && isProfile,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 20) return undefined;
      return lastPage[lastPage.length - 1].last_time;
    },
    initialPageParam: 0,
  });

  const isSharedChat =
    pathname?.includes('messages') && pathname.split('/').pop() !== 'messages';
  const sharedChatId = pathname?.split('/').pop();
  const { data: sharedChat } = useQuery({
    queryKey: ['sharedChat', sharedChatId],
    queryFn: () => getChatDocumentById(sharedChatId as string),
    enabled: !!sharedChatId && isSharedChat,
  });
  const loadMore = () => {
    if (isOrderDetail && order) return; /// all chats loaded
    if (isSharedChat && sharedChat) return; // only one chat here and its loaded
    if (isProfile && hasNextPageForProfile && !isFetchingNextPageForProfile) {
      fetchNextPageForProfile();
    } else if (isFilteredOffers && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    } else if (hasMore && !isLoading) {
      setLimit((prev) => prev + 20);
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const stateNum = {
    offers_pending: 0,
    offers_pending_received: 0,
    offers_pending_sent: 0,
    offers_accepted: 1,
    offers_declined: 2,
    offers_expired: 3,
  } as const;
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['offers', userDoc?.uid, tab],
      queryFn: ({ pageParam }) =>
        getOffersByStatePaginated({
          userDoc,
          state: stateNum[tab as keyof typeof stateNum],
          last: pageParam as number,
          l: limit,
        }),
      getNextPageParam: (lastPage) => {
        if (lastPage.length < limit) return undefined;
        return lastPage[lastPage.length - 1].created;
      },
      initialPageParam: 0,
      enabled: isFilteredOffers,
    });

  // Direction is resolved from the chat messages rather than the offer query, so a page of
  // offers can come back mostly filtered out and the offer cursor has to drive the end of list
  const hasMore = isFilteredOffers ? !!hasNextPage : chats.length === limit;

  const isSearching = !!debouncedSearchQuery;
  const searchesChatList = !isOrderDetail && !isSharedChat && !isProfile;

  const { data: searchWindowChats = [], isFetching: isFetchingSearchWindow } =
    useQuery({
      queryKey: ['chatSearchWindow', userDoc?.uid, tab],
      queryFn: () => getChatsForSearchWindow({ userDoc, mainFilter: tab }),
      enabled: isSearching && searchesChatList && !!userDoc?.uid,
      staleTime: CHAT_SEARCH_STALE_TIME,
    });

  // whatever the list is currently showing for this route
  const listedChats = useMemo(() => {
    if (isOrderDetail) return chatsForOrderDetail;
    if (isProfile) return chatsForProfile?.pages.flat() ?? [];
    if (!offerDirection) return chats;
    return chats.filter((chat) =>
      matchesOfferDirection({
        chat,
        uid: userDoc?.uid,
        direction: offerDirection,
      })
    );
  }, [
    isOrderDetail,
    chatsForOrderDetail,
    isProfile,
    chatsForProfile,
    chats,
    offerDirection,
    userDoc?.uid,
  ]);

  const searchPool = useMemo(() => {
    const byId = new Map<string, ChatDocument>();
    for (const chat of [...listedChats, ...searchWindowChats]) {
      if (byId.has(chat.id)) continue;
      if (
        !matchesOfferDirection({
          chat,
          uid: userDoc?.uid,
          direction: offerDirection,
        })
      )
        continue;
      byId.set(chat.id, chat);
    }
    return [...byId.values()];
  }, [listedChats, searchWindowChats, offerDirection, userDoc?.uid]);

  const { data: searchTerms = {}, isFetching: isFetchingSearchTerms } =
    useQuery({
      queryKey: [
        'chatSearchTerms',
        userDoc?.uid,
        searchPool.map((chat) => chat.id),
      ],
      queryFn: () => getChatSearchTerms(searchPool, userDoc!.uid),
      enabled: isSearching && !!userDoc?.uid && searchPool.length > 0,
      staleTime: CHAT_SEARCH_STALE_TIME,
    });

  const { data: chatsByNumber = [], isFetching: isFetchingChatsByNumber } =
    useQuery({
      queryKey: ['chatsByOrderOrCaseNum', userDoc?.uid, debouncedSearchQuery],
      queryFn: () => getChatsByOrderOrCaseNumber(debouncedSearchQuery, userDoc),
      enabled: isSearching && !!userDoc?.uid,
    });

  const chatBySearchQuery = useMemo(() => {
    if (!isSearching) return [];
    const tokens = normalizeChatSearchQuery(debouncedSearchQuery);
    const matches = searchPool.filter((chat) =>
      chatMatchesSearchQuery({
        chat,
        uid: userDoc?.uid,
        tokens,
        extraTerms: searchTerms[chat.id],
      })
    );
    const byId = new Map<string, ChatDocument>();
    for (const chat of [...matches, ...chatsByNumber]) {
      if (byId.has(chat.id)) continue;
      if (
        !matchesOfferDirection({
          chat,
          uid: userDoc?.uid,
          direction: offerDirection,
        })
      )
        continue;
      byId.set(chat.id, chat);
    }
    return [...byId.values()];
  }, [
    isSearching,
    debouncedSearchQuery,
    searchPool,
    searchTerms,
    chatsByNumber,
    offerDirection,
    userDoc?.uid,
  ]);

  const isSearchLoading =
    isSearching &&
    (isFetchingSearchWindow ||
      isFetchingSearchTerms ||
      isFetchingChatsByNumber);

  useEffect(() => {
    if (!userDoc) return;
    if (isOrderDetail || isSharedChat || isProfile) return; // these chats are already loaded
    if (isFilteredOffers) {
      const ids = data?.pages
        .flatMap((page) => page.map((offer) => offer.chat_id ?? ''))
        .filter((id): id is string => id !== undefined);
      if (!ids?.length) return;
      setIsLoading(true);

      // first 30 are realtime, rest are just fetched
      const realtimeIds = ids?.slice(0, 30);
      const q = query(
        messagesRef,
        where('id', 'in', realtimeIds),
        ...(userDoc.roles?.support
          ? []
          : [where('uids', 'array-contains', userDoc.uid)])
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const chats = snapshot.docs.map((doc) => doc.data() as ChatDocument);
        const remainingChats = ids?.slice(30);
        if (remainingChats?.length) {
          getChatsByIds(remainingChats, userDoc).then((remainingChats) => {
            setChats([...chats, ...remainingChats]);
            setIsLoading(false);
          });
        } else {
          setChats(chats);
          setIsLoading(false);
        }
      });
      return () => {
        unsubscribe();
      };
    } else {
      setIsLoading(true);

      const chatsQuery = getChatsQuery({
        userDoc,
        mainFilter: tab,
        numOfChats: limit,
      });
      const unsubscribe = onSnapshot(chatsQuery, (snapshot) => {
        const chats = snapshot.docs.map((doc) => doc.data() as ChatDocument);
        setChats(chats);
        setIsLoading(false);
      });
      return () => {
        unsubscribe();
      };
    }
  }, [tab, userDoc, limit, isFilteredOffers, data?.pages, isOrderDetail]);
  return {
    chats: (isSharedChat && sharedChat
      ? [sharedChat]
      : debouncedSearchQuery
      ? chatBySearchQuery
      : listedChats
    ).sort((a, b) => b.last_time - a.last_time), // FE filter since unread is not sorted in query
    loadMore,
    hasMore,
    isLoading: isLoading || isSearchLoading,
  };
};
